import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarRail,
  useSidebar
} from "@/components/ui/sidebar";
import { ScrollArea } from "@/components/ui/scroll-area";
import nytecaIcon from "@/assets/nyteca-icon.svg";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth.store";
import { useChatsStore } from "@/stores/chats.store";
import { useProjectsStore } from "@/stores/projects.store";
import type { ReadChatDto, ReadProjectWithRelationsDto } from "@package/types";
import {
  Link,
  useNavigate,
  useParams,
  useRouterState
} from "@tanstack/react-router";
import {
  Folder,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  Plus,
  Share2,
  Trash2
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SidebarUserMenu } from "./sidebar-user-menu";

interface AppSidebarProps {
  onDeleteChat: (chatId: string) => void;
  onShareChat: (chatId: string) => void;
}

export function AppSidebar({ onDeleteChat, onShareChat }: AppSidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const pathname = useRouterState({
    select: (routerState) => routerState.location.pathname
  });
  const isCollapsed = state === "collapsed";

  const chats = useChatsStore((s) => s.chats);
  const isLoadingChats = useChatsStore((s) => s.isLoading);
  const addChat = useChatsStore((s) => s.addChat);
  const updateChat = useChatsStore((s) => s.updateChat);
  const sendingChatIds = useChatsStore((s) => s.sendingChatIds);

  const projects = useProjectsStore((s) => s.projects);
  const updateProjectChat = useProjectsStore((s) => s.updateProjectChat);

  const canWrite = useAuthStore((s) => s.user?.canWrite ?? false);

  const [isCreating, setIsCreating] = useState(false);

  const params = useParams({ strict: false });
  const currentChatId = (params as { chatId?: string }).chatId;

  const recentChats = useMemo(
    () =>
      [...chats].sort(
        (left, right) =>
          new Date(right.updatedAt).getTime() -
          new Date(left.updatedAt).getTime()
      ),
    [chats]
  );

  const createNewChat = async () => {
    try {
      setIsCreating(true);
      const newChat = await api.post<ReadChatDto>("/chats");
      addChat(newChat);
      updateProjectChat(newChat);
      navigate({ to: "/chats/$chatId", params: { chatId: newChat.id } });
    } catch (error) {
      console.error("Failed to create chat:", error);
      toast.error(t("chats.createError"));
    } finally {
      setIsCreating(false);
    }
  };

  const updateChatProject = async (
    chat: ReadChatDto,
    projectId: string | null
  ) => {
    try {
      const updatedChat = await api.patch<ReadChatDto>(
        `/chats/${chat.id}/project`,
        { projectId }
      );
      updateChat(updatedChat);
      updateProjectChat(updatedChat);
      toast.success(
        projectId ? t("projects.chatAdded") : t("projects.chatRemoved")
      );
    } catch (error) {
      console.error("Failed to update chat project:", error);
      toast.error(t("projects.chatUpdateError"));
    }
  };

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarHeader className="pb-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                size="lg"
                tooltip={t("common.appName")}
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Link to="/chats">
                  <img
                    src={nytecaIcon}
                    alt={t("common.appName")}
                    className="size-8 shrink-0"
                  />
                  <span className="truncate font-semibold">
                    {t("common.appName")}
                  </span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent className="overflow-hidden">
          <ScrollArea className="min-h-0 flex-1 overflow-hidden">
            <SidebarGroup className="w-0 min-w-full">
              <SidebarGroupContent>
                <SidebarMenu>
                  {canWrite && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={createNewChat}
                        disabled={isCreating}
                        tooltip={t("chats.newChat")}
                      >
                        {isCreating ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Plus className="size-4" />
                        )}
                        <span>
                          {isCreating
                            ? t("chats.creating")
                            : t("chats.newChat")}
                        </span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}

                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname.startsWith("/projects")}
                      tooltip={t("projects.title")}
                    >
                      <Link to="/projects">
                        <Folder className="size-4" />
                        <span>{t("projects.title")}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup className="mt-2 w-0 min-w-full">
              <SidebarGroupLabel>{t("chats.recentlyUsed")}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {isLoadingChats ? (
                    <>
                      <SidebarMenuSkeleton showIcon />
                      <SidebarMenuSkeleton showIcon />
                      <SidebarMenuSkeleton showIcon />
                    </>
                  ) : recentChats.length === 0 ? (
                    !isCollapsed && (
                      <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                        {t("chats.noChats")}
                      </div>
                    )
                  ) : (
                    recentChats.map((chat) => (
                      <ChatMenuItem
                        key={chat.id}
                        chat={chat}
                        projects={projects}
                        currentChatId={currentChatId}
                        isSending={
                          sendingChatIds.has(chat.id) ||
                          chat.pendingSince != null
                        }
                        onDeleteChat={onDeleteChat}
                        onShareChat={onShareChat}
                        onUpdateChatProject={updateChatProject}
                      />
                    ))
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </ScrollArea>
        </SidebarContent>

        <SidebarFooter>
          <SidebarUserMenu />
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>
    </>
  );
}

interface ChatMenuItemProps {
  chat: ReadChatDto;
  projects: ReadProjectWithRelationsDto[];
  currentChatId?: string;
  isSending: boolean;
  onDeleteChat: (chatId: string) => void;
  onShareChat: (chatId: string) => void;
  onUpdateChatProject: (chat: ReadChatDto, projectId: string | null) => void;
}

function ChatMenuItem({
  chat,
  projects,
  currentChatId,
  isSending,
  onDeleteChat,
  onShareChat,
  onUpdateChatProject
}: ChatMenuItemProps) {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const canWrite = useAuthStore((s) => s.user?.canWrite ?? false);
  const isProjectChat = !!chat.projectId;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={currentChatId === chat.id}
        tooltip={chat.title || t("chats.noTitle")}
      >
        <Link to="/chats/$chatId" params={{ chatId: chat.id }}>
          {isSending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <MessageSquare className="size-4" />
          )}
          <span>{chat.title || t("chats.noTitle")}</span>
        </Link>
      </SidebarMenuButton>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction showOnHover>
            <MoreHorizontal className="size-4" />
            <span className="sr-only">{t("chats.actions")}</span>
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side={isMobile ? "bottom" : "right"}>
          {canWrite && (
            <>
              {isProjectChat ? (
                <DropdownMenuItem
                  onClick={() => onUpdateChatProject(chat, null)}
                >
                  <Folder className="size-4" />
                  {t("projects.removeChat")}
                </DropdownMenuItem>
              ) : projects.length > 0 && isMobile ? (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    {t("projects.addChat")}
                  </DropdownMenuLabel>
                  {projects.map((project) => (
                    <DropdownMenuItem
                      key={project.id}
                      onClick={() => onUpdateChatProject(chat, project.id)}
                    >
                      {project.name}
                    </DropdownMenuItem>
                  ))}
                </>
              ) : projects.length > 0 ? (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Folder className="size-4" />
                    {t("projects.addChat")}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {projects.map((project) => (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => onUpdateChatProject(chat, project.id)}
                      >
                        {project.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : (
                <DropdownMenuItem disabled>
                  <Folder className="size-4" />
                  {t("projects.noProjects")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onShareChat(chat.id)}>
                <Share2 className="size-4" />
                {t("chats.share")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem
            variant="destructive"
            onClick={() => onDeleteChat(chat.id)}
          >
            <Trash2 className="size-4" />
            {t("chats.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
